/**
 * Typy pojazdów w kartotece klienta.
 *
 * ## Dlaczego naczepy nie mają własnego zasobu
 *
 * Bo klient nie ma ich osobno. Jego interfejs wystawia **jeden endpoint
 * pojazdów**, a naczepy i wózki widłowe siedzą w nim razem z ciągnikami,
 * rozróżnione wyłącznie typem. Zmierzone na danych z 22.09.2026:
 *
 * | typ | ile |
 * |---|---|
 * | JUMBO TRUCK | 586 |
 * | TRAILER | 580 |
 * | FORKLIFT | 39 |
 * | OTHER | 5 |
 * | SOLO, MEGA TRUCK | po 1 |
 *
 * Tabela `Trailer` w schemacie (port z FreightFusion) ma **zero wierszy** i nic
 * jej nie zapełnia. Ekran zbudowany na niej pokazywałby pustą listę i wyglądał
 * na zepsuty, choć naczepy są w systemie — tyle że w kartotece pojazdów.
 *
 * Dlatego „Naczepy" to **widok na kartotekę pojazdów zawężony typem**, a nie
 * osobny byt. Gdy klient kiedyś wystawi naczepy osobno, zmieni się źródło,
 * nie ekran.
 */

/** Typy, które NIE stają pod zlecenie — kopia z `cars/filtry.ts`, pilnuje test. */
export const TYPE_TRAILER = 'TRAILER';
export const TYPE_FORKLIFT = 'FORKLIFT';

/** Komplet typów spoza podpowiedzi — kopia `TYPES_NOT_FOR_ORDERS` z backendu. */
export const TYPES_NOT_FOR_ORDERS: readonly string[] = [TYPE_TRAILER, TYPE_FORKLIFT];

/**
 * Typ z TMS do wyświetlenia (poprawa brzmienia 01.10.2026). Wartość w danych
 * i w filtrach zostaje — planista zna nazwy typów z TMS, więc tłumaczymy
 * tylko zbiorcze „OTHER”, które po angielsku nic nie mówi.
 */
const VEHICLE_TYPE_LABELS: ReadonlyMap<string, string> = new Map([['OTHER', 'inny typ']]);

export function vehicleTypeLabel(carType: unknown): string {
  const raw = typeof carType === 'string' ? carType : '';
  return VEHICLE_TYPE_LABELS.get(raw) ?? raw;
}

/**
 * Czy pojazd jest autem pod zlecenie — ta sama reguła co `loadFleet`
 * w `vehicle-suggestions.service.ts`: typ spoza listy albo brak typu (auto
 * bez typu wchodzi do podpowiedzi z ostrzeżeniem). Naczepa i wózek nie.
 * Karta naczepy pokazywała przycisk liczenia zleceń, który kończył się 404
 * (e2e na dev, 28.09.2026).
 */
export function isOrderVehicle(carType: unknown): boolean {
  if (carType === null || carType === undefined || carType === '') return true;
  return !TYPES_NOT_FOR_ORDERS.includes(String(carType));
}

/** Filtr listy naczep: to samo zapytanie co kartoteka pojazdów, zawężone. */
export const TRAILER_FILTER = { carType: TYPE_TRAILER } as const;
